import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChefHat, Info, Heart, MapPin, Star, X } from 'lucide-react';
import axios from 'axios';
import Lightbox from '../components/Lightbox';
import { useLightbox } from '../hooks/useLightbox';
import { API } from '../utils/api';

const PatesFraiches = () => {
  const lightbox = useLightbox();
  const [dynamicProducts, setDynamicProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // États pour la modale
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Produits statiques
  const staticProducts = [
    {
      id: 'linguini',
      title: 'Linguini fraîches',
      description: 'Pâtes longues traditionnelles, parfaites avec nos sauces maison',
      image: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/zqljq8s2_image00006.jpeg',
      cookingTime: 'Cuisson : 2-3 min'
    },
    {
      id: 'ravioli-ricotta',
      title: 'Ravioli ricotta épinards',
      description: 'Ravioli farcis à la ricotta fraîche et épinards, selon la tradition',
      image: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/lrbxoa1f_image00010.jpeg',
      cookingTime: 'Cuisson : 7-10 min'
    },
    {
      id: 'demi-lune',
      title: 'Demi lune à la viande',
      description: 'Demi lune traditionnelles farcies à la viande, spécialité de l\'Émilie-Romagne',
      image: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/16l3psz1_image00002.jpeg',
      cookingTime: 'Cuisson : 7-10 min'
    },
    {
      id: 'fusilli',
      title: 'Fusilli fraîches',
      description: 'Pâtes en spirale parfaites pour retenir les sauces et accompagnements',
      image: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/8cfgf2ku_image00012.jpeg',
      cookingTime: 'Cuisson : 2-3 min'
    },
    {
      id: 'ravioli-champignons',
      title: 'RAVIOLI CHAMPIGNONS',
      description: 'Ravioli farcis aux champignons des bois',
      image: 'https://customer-assets.emergentagent.com/job_trullo-clone/artifacts/psq5g682_ChatGPT%20Image%2029%20oct.%202025%2C%2011_26_34.png',
      cookingTime: 'Cuisson : 7-10 min'
    },
    {
      id: 'girelli',
      title: 'GIRELLI JAMBON CRU',
      description: 'Girelli farcis au jambon cru présent dans la vitrine',
      image: 'https://customer-assets.emergentagent.com/job_trullo-clone/artifacts/75fstz67_IMG_1508.jpeg',
      cookingTime: 'Cuisson : 10-12 min'
    }
  ];

  useEffect(() => {
    fetchDynamicProducts();
  }, []);

  const fetchDynamicProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?category=pates`);
      setDynamicProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };
  
  return (
    <main className="min-h-screen">
      <div className="pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 py-16">
          <div className="container mx-auto transition-all duration-700 opacity-100 translate-y-0">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="heading-xl mb-6 text-gray-900 animate-fade-in-text animation-delay-0">
                Pâtes fraîches maison
              </h1>
              <p className="text-lead text-gray-700 mb-6 animate-fade-in-text animation-delay-150">
                Notre atelier de pâtes prépare chaque semaine des pâtes fraîches avec des ingrédients choisis. Nous vous conseillons sur la cuisson, les sauces et les fromages qui les subliment.
              </p>
              <p className="text-body text-gray-600 mb-8 animate-fade-in-text animation-delay-300">
                Nos pâtes fraîches sont façonnées chaque semaine dans notre atelier, avec une semoule de qualité et un savoir-faire artisanal. Nous préparons des formats classiques et des recettes de saison.
              </p>
            </div>
          </div>
        </section>

        {/* Spécialités Section */}
        <section className="section bg-white">
          <div className="container mx-auto transition-all duration-700 opacity-100 translate-y-0">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10">
              {/* Produits statiques mappés */}
              {staticProducts.map((product) => (
                <div 
                  key={product.id}
                  className="card card-hover-elegant cursor-pointer" 
                  onClick={() => {
                    setSelectedProduct(product);
                    setShowProductModal(true);
                  }}
                >
                  <img
                    alt={product.title}
                    className="w-full h-48 object-cover"
                    src={product.image}
                  />
                  <div className="card-body">
                    <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                    <p className="text-body text-gray-600 mb-4">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">{product.cookingTime}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* OLD - to be removed - Linguini */}
              <div className="card card-hover-elegant" style={{display: 'none'}}>
                <img
                  alt="Linguini fraîches"
                  className="w-full h-48 object-cover image-clickable"
                  src="https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/zqljq8s2_image00006.jpeg"
                  onClick={() => lightbox.openLightbox([{ src: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/zqljq8s2_image00006.jpeg', alt: 'Linguini fraîches' }], 0)}
                />
                <div className="card-body">
                  <h3 className="heading-sm mb-3 text-gray-900">Linguini fraîches</h3>
                  <p className="text-body text-gray-600 mb-4">
                    Pâtes longues traditionnelles, parfaites avec nos sauces maison
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Cuisson : 2-3 min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ravioli */}
              <div className="card card-hover-elegant">
                <img
                  alt="Ravioli ricotta épinards"
                  className="w-full h-48 object-cover image-clickable"
                  src="https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/lrbxoa1f_image00010.jpeg"
                  onClick={() => lightbox.openLightbox([{ src: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/lrbxoa1f_image00010.jpeg', alt: 'Ravioli ricotta épinards' }], 0)}
                />
                <div className="card-body">
                  <h3 className="heading-sm mb-3 text-gray-900">Ravioli ricotta épinards</h3>
                  <p className="text-body text-gray-600 mb-4">
                    Ravioli farcis à la ricotta fraîche et épinards, selon la tradition
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Cuisson : 7-10 min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Demi lune */}
              <div className="card card-hover-elegant">
                <img
                  alt="Demi lune à la viande"
                  className="w-full h-48 object-cover image-clickable"
                  src="https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/16l3psz1_image00002.jpeg"
                  onClick={() => lightbox.openLightbox([{ src: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/16l3psz1_image00002.jpeg', alt: 'Demi lune à la viande' }], 0)}
                />
                <div className="card-body">
                  <h3 className="heading-sm mb-3 text-gray-900">Demi lune à la viande</h3>
                  <p className="text-body text-gray-600 mb-4">
                    Demi lune traditionnelles farcies à la viande, spécialité de l'Émilie-Romagne
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Cuisson : 7-10 min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fusilli */}
              <div className="card card-hover-elegant">
                <img
                  alt="Fusilli fraîches"
                  className="w-full h-48 object-cover image-clickable"
                  src="https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/8cfgf2ku_image00012.jpeg"
                  onClick={() => lightbox.openLightbox([{ src: 'https://customer-assets.emergentagent.com/job_trullo-epicerie/artifacts/8cfgf2ku_image00012.jpeg', alt: 'Fusilli fraîches' }], 0)}
                />
                <div className="card-body">
                  <h3 className="heading-sm mb-3 text-gray-900">Fusilli fraîches</h3>
                  <p className="text-body text-gray-600 mb-4">
                    Pâtes en spirale parfaites pour retenir les sauces et accompagnements
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Cuisson : 2-3 min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ravioli Champignons */}
              <div className="card card-hover-elegant">
                <img
                  alt="Ravioli champignons"
                  className="w-full h-48 object-cover image-clickable"
                  src="https://customer-assets.emergentagent.com/job_trullo-clone/artifacts/psq5g682_ChatGPT%20Image%2029%20oct.%202025%2C%2011_26_34.png"
                  onClick={() => lightbox.openLightbox([{ src: 'https://customer-assets.emergentagent.com/job_trullo-clone/artifacts/psq5g682_ChatGPT%20Image%2029%20oct.%202025%2C%2011_26_34.png', alt: 'Ravioli champignons' }], 0)}
                />
                <div className="card-body">
                  <h3 className="heading-sm mb-3 text-gray-900">RAVIOLI CHAMPIGNONS</h3>
                  <p className="text-body text-gray-600 mb-4">
                    Ravioli farcis aux champignons des bois
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Cuisson : 7-10 min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Girelli Jambon Cru */}
              <div className="card card-hover-elegant">
                <img
                  alt="Girelli jambon cru"
                  className="w-full h-48 object-cover image-clickable"
                  src="https://customer-assets.emergentagent.com/job_trullo-clone/artifacts/75fstz67_IMG_1508.jpeg"
                  onClick={() => lightbox.openLightbox([{ src: 'https://customer-assets.emergentagent.com/job_trullo-clone/artifacts/75fstz67_IMG_1508.jpeg', alt: 'Girelli jambon cru' }], 0)}
                />
                <div className="card-body">
                  <h3 className="heading-sm mb-3 text-gray-900">GIRELLI JAMBON CRU</h3>
                  <p className="text-body text-gray-600 mb-4">
                    Girelli farcis au jambon cru présent dans la vitrine
                  </p>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium text-green-700">Cuisson : 10-12 min</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Products from Admin */}
              {dynamicProducts.length > 0 && dynamicProducts.map((product) => (
                <div 
                  key={product.id} 
                  className="card card-hover-elegant cursor-pointer"
                  onClick={() => {
                    setSelectedProduct({
                      title: product.title,
                      description: product.description,
                      image: product.image,
                      cookingTime: product.cookingTimeOrAdvice
                    });
                    setShowProductModal(true);
                  }}
                >
                  <img
                    alt={product.title}
                    className="w-full h-48 object-cover"
                    src={product.image}
                  />
                  <div className="card-body">
                    <h3 className="heading-sm mb-3 text-gray-900">{product.title}</h3>
                    <p className="text-body text-gray-600 mb-4">
                      {product.description}
                    </p>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-700">{product.cookingTimeOrAdvice}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Message si chargement */}
            {loading && dynamicProducts.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-600">Chargement des produits supplémentaires...</p>
              </div>
            )}
          </div>
        </section>

        {/* Comment les cuire Section */}
        <section className="section bg-gray-50">
          <div className="container mx-auto">
            <div className="text-center mb-12">
              <h2 className="heading-lg mb-4">Comment les cuire ?</h2>
              <p className="text-lead text-gray-600 max-w-2xl mx-auto">
                Nos conseils pour une cuisson parfaite de vos pâtes fraîches maison
              </p>
            </div>
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <div className="bg-green-50 p-6 rounded-lg border-l-4 border-green-500">
                  <div className="flex items-start gap-3">
                    <Info className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-green-800 mb-2">Conseil du chef</h4>
                      <p className="text-body text-green-700">
                        Les pâtes fraîches cuisent plus rapidement que les pâtes sèches. Surveillez la cuisson et goûtez régulièrement pour obtenir la texture « al dente » parfaite.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h4 className="heading-sm mb-4 text-gray-900">Instructions de cuisson</h4>
                  <ol className="space-y-3 text-body text-gray-600">
                    <li className="flex gap-3">
                      <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        1
                      </span>
                      <span>Porter à ébullition une grande casserole d'eau salée (10g de sel par litre)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        2
                      </span>
                      <span>Plonger les pâtes dans l'eau bouillante et remuer délicatement</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        3
                      </span>
                      <span>Cuire selon le temps indiqué en remuant occasionnellement</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                        4
                      </span>
                      <span>Égoutter et servir immédiatement avec votre sauce préférée</span>
                    </li>
                  </ol>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-lg">
                  <h4 className="heading-sm mb-4 text-gray-900">Nos suggestions d'accompagnement</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      'Sauce tomate basilic',
                      'Pesto de basilic',
                      'Beurre et parmesan',
                      'Crème aux champignons',
                      'Sauce bolognaise',
                      "Huile d'olive et ail"
                    ].map((sauce, index) => (
                      <div key={index} className="flex items-center gap-2 text-body text-gray-700">
                        <Heart className="w-4 h-4 text-red-500" />
                        <span>{sauce}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-red-50 p-6 rounded-lg shadow-lg border-l-4 border-red-500">
                  <h4 className="heading-sm mb-4 text-red-900">Commande &amp; disponibilité</h4>
                  <div className="space-y-3 text-body text-red-800">
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Production chaque mardi et vendredi</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Commande recommandée pour les grosses quantités</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-red-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span>Possibilité de préparation sur demande pour vos événements</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section bg-gradient-to-r from-green-700 to-green-600 text-white">
          <div className="container mx-auto text-center transition-all duration-700 opacity-100 translate-y-0">
            <h2 className="heading-lg mb-6">Prêt à goûter nos pâtes fraîches ?</h2>
            <p className="text-lead mb-8 opacity-90 max-w-2xl mx-auto">
              Passez en boutique ou appelez-nous pour découvrir nos pâtes du jour et passer commande. Notre équipe se fera un plaisir de vous conseiller !
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/infos-pratiques" className="btn bg-white text-green-700 hover:bg-gray-100 btn-lg">
                <MapPin className="w-5 h-5" />
                Venir en boutique
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* Modal Produit */}
      {showProductModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Détails du produit</h3>
              <button
                onClick={() => {
                  setShowProductModal(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Image en grand */}
              {selectedProduct.image && (
                <div className="w-full rounded-lg overflow-hidden">
                  <img
                    src={selectedProduct.image}
                    alt={selectedProduct.title}
                    className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                  />
                </div>
              )}

              {/* Titre */}
              <div>
                <h4 className="text-3xl font-bold text-gray-900">{selectedProduct.title}</h4>
              </div>

              {/* Description complète */}
              <div>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {selectedProduct.description}
                </p>
              </div>

              {/* Temps de cuisson */}
              <div className="pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-500 mb-2">TEMPS DE CUISSON</h4>
                <div className="flex items-center gap-2 text-gray-900">
                  <Clock className="w-5 h-5 text-green-600" />
                  <span className="text-lg font-medium text-green-700">{selectedProduct.cookingTime}</span>
                </div>
              </div>

              {/* Bouton fermer */}
              <div className="flex justify-center pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowProductModal(false);
                    setSelectedProduct(null);
                  }}
                  className="px-8 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-lg font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox.isOpen && (
        <Lightbox
          images={lightbox.images}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.closeLightbox}
          onPrevious={lightbox.previousImage}
          onNext={lightbox.nextImage}
        />
      )}
    </main>
  );
};

export default PatesFraiches;